import { useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import { ROLES } from '@/constants';
import { operationalDashboardService } from '../services/operationalDashboardService';
import { OperationalDashboardAccessContext } from './OperationalDashboardAccessContext';

/**
 * Resolves the current account's dashboard grant once per session.
 *
 * Only the roles that could plausibly hold one are asked — a member or a
 * project manager never triggers the request at all. Everyone else gets a
 * single call whose answer the whole tree reads, rather than each layout
 * asking again.
 *
 * A failed lookup resolves to "no access": the nav entry quietly stays hidden,
 * and the server would refuse the page regardless. Failing open here would
 * advertise a link that 403s.
 */
const ELIGIBLE_ROLES = new Set([
    ROLES.ADMIN,
    ROLES.SUPER_ADMIN,
    ROLES.EXECUTIVE,
    ROLES.TASK_FORCE_CHAIR,
    ROLES.PILLAR_CHAIR,
]);

/** Nothing resolved yet. `accountId: null` never matches a real account. */
const UNRESOLVED = { accountId: null, hasAccess: false };

export function OperationalDashboardAccessProvider({ children }) {
    const { user, isAuthenticated } = useAuthContext();

    // The answer is stamped with the account it was fetched for, so a stale
    // result from a previous sign-in can never be read as the current user's.
    // State is written only from the promise callbacks: setting it in the
    // effect body would be a cascading render.
    const [resolved, setResolved] = useState(UNRESOLVED);

    const eligible = Boolean(isAuthenticated && ELIGIBLE_ROLES.has(user?.role));
    const accountId = user?.accountId ?? null;

    useEffect(() => {
        if (!eligible || !accountId) return undefined;

        let cancelled = false;
        operationalDashboardService
            .getMyAccess()
            .then((granted) => {
                if (!cancelled) setResolved({ accountId, hasAccess: granted });
            })
            .catch(() => {
                if (!cancelled) setResolved({ accountId, hasAccess: false });
            });

        return () => {
            cancelled = true;
        };
    }, [eligible, accountId]);

    const value = useMemo(() => {
        const isCurrent = accountId != null && resolved.accountId === accountId;
        return {
            hasAccess: eligible && isCurrent && resolved.hasAccess,
            // Ineligible accounts are never "loading" — there is nothing to wait
            // for, so a layout can render its nav immediately.
            loading: eligible && !isCurrent,
        };
    }, [eligible, accountId, resolved]);

    return (
        <OperationalDashboardAccessContext.Provider value={value}>
            {children}
        </OperationalDashboardAccessContext.Provider>
    );
}

export default OperationalDashboardAccessProvider;
